import { useLocation } from "@reach/router";
import { graphql, useStaticQuery } from "gatsby";
import { useCallback, useMemo } from "react";

import type { TFunction } from "i18next";

const ROOT_I18NEXT_QUERY = graphql`
  query RootI18nextQuery {
    allLocale {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;

interface LocaleNode {
  ns: string;
  data: string; // stringified JSON
  language: string;
}

interface AllLocale {
  edges: { node: LocaleNode }[];
}

interface UseRootI18nextProps {
  language: string;
  routed: boolean;
  languages: string[];
  originalPath: string;
  t: TFunction;
}

const locales = process.env.LOCALE_LANGUAGES ? process.env.LOCALE_LANGUAGES : "en";
const localesArray = locales.split(",").filter(Boolean);
const DEFAULT_LANG = "en";

const getOriginalPath = (pathname: string, matchedLanguage?: string) => {
  if (pathname === `/${matchedLanguage}`) {
    return "/";
  } else if (matchedLanguage) {
    return pathname.replace(`/${matchedLanguage}`, "");
  } else {
    return pathname;
  }
};

const toArray = <T>(v: T | T[] | null | undefined): T[] => {
  if (!v) return [];

  return Array.isArray(v) ? v : [v];
};

/**
 * useRootI18next
 *
 * Lightweight translation hook that mirrors key behaviors of gatsby-plugin-react-i18next
 * without requiring the provider wrapper (useful for components rendered above the wrapper).
 *
 * Features:
 * - Detects routed language from the URL (e.g., "/es/..."), or falls back to LOCALE_DEFAULT/"en".
 * - Accepts one or multiple namespaces; auto-includes any available namespaces containing "common".
 * - Resolves dot-path keys. If no namespaces are provided, expects fully-qualified keys: "<ns>.<path>".
 * - Fallback chain: languageToUse → DEFAULT_LANG → options.defaultValue → input key.
 * - Basic interpolation: replaces {{var}} from options.
 * - Memoizes parsed namespace JSON and the translator function.
 *
 * @param ns Optional namespace or ordered list of namespaces to search. If omitted, keys must be fully-qualified.
 * @returns {UseRootI18nextProps} language info and a t() function.
 *
 * @example
 * // With namespaces (relative keys)
 * const { t } = useRootI18next(["navigation", "common-phrases"]);
 * t("commonShopAll", { defaultValue: "Shop All" });
 *
 * @example
 * // Without namespaces (fully-qualified keys)
 * const { t } = useRootI18next();
 * t("common-phrases.commonShopAll", { defaultValue: "Shop All" });
 */
export const useRootI18next = (ns?: string | string[]): UseRootI18nextProps => {
  /**
   * Phase 1: Resolve routing and language context
   */
  const { pathname } = useLocation();
  const { allLocale } = useStaticQuery<{ allLocale: AllLocale }>(ROOT_I18NEXT_QUERY);

  const matchedLanguage = localesArray.find((locale) => pathname.startsWith(`/${locale}`));
  const detectedLanguage = matchedLanguage || DEFAULT_LANG;
  const routed = Boolean(matchedLanguage);
  const originalPath = getOriginalPath(pathname, matchedLanguage);
  const languageToUse = matchedLanguage || DEFAULT_LANG;

  /**
   * Phase 2: Build namespace list (user-provided + any "common*" namespaces found)
   */
  const userNamespaces = toArray(ns);
  const availableNamespaces = Array.from(
    new Set((allLocale?.edges ?? []).map((e) => e?.node?.ns).filter(Boolean)),
  );
  const commonNamespaces = availableNamespaces.filter((n) => n.toLowerCase().includes("common"));
  const namespaces: string[] = Array.from(new Set([...userNamespaces, ...commonNamespaces]));

  /**
   * Helpers
   */
  const findLocale = (edges: AllLocale["edges"], namespace: string, lang: string) =>
    edges.find((e) => e?.node?.ns === namespace && e?.node?.language === lang);

  const findLocaleEdge = useCallback(
    (namespace: string, lang: string, fallbackLang: string): { node: LocaleNode } | undefined => {
      let edge = findLocale(allLocale?.edges, namespace, lang);
      if (!edge && lang !== fallbackLang) {
        edge = findLocale(allLocale?.edges, namespace, fallbackLang);
      }

      return edge;
    },
    [allLocale],
  );

  const traversePath = (obj: unknown, segments: string[]): unknown =>
    segments.reduce<unknown>((acc, seg) => {
      if (acc && typeof acc === "object" && seg in (acc as Record<string, unknown>)) {
        return (acc as Record<string, unknown>)[seg];
      }

      return undefined;
    }, obj);

  // Phase 3: Preload and memoize parsed translations per namespace for the active language
  const translationsByNs = useMemo(() => {
    const map = new Map<string, unknown>();
    for (const n of namespaces) {
      const edge = findLocaleEdge(n, languageToUse, DEFAULT_LANG);
      if (!edge) continue;
      try {
        map.set(n, JSON.parse(edge.node.data));
      } catch {
        // In development, surface parsing issues to aid debugging; noop in production
        if (process.env.NODE_ENV !== "production") {
          console.warn(`[i18n] Failed to parse namespace "${n}" for language "${languageToUse}"`);
        }
        // ignore parse errors for this namespace so resolution can try other namespaces
      }
    }

    return map;
  }, [namespaces, languageToUse, findLocaleEdge]);

  /**
   * Phase 4: Memoized translator function
   *
   * Behavior:
   * - If namespaces are provided, treat keys as relative to each namespace and try them in order.
   * - If no namespaces are provided, expect fully-qualified keys: "<ns>.<path.to.key>".
   * - Resolves for routed language or falls back to DEFAULT_LANG.
   * - Supports defaultValue in options and basic {{var}} interpolation.
   */
  const t = useCallback(
    (inputKey?: string, options?: Record<string, unknown>): string | undefined => {
      // If no key and namespaces provided, we can't resolve anything
      if (!inputKey) return undefined;

      const parts = inputKey.split(".");

      // Build resolution attempts: either provided namespaces (relative key) or ns from key (fully-qualified)
      const attempts: { ns: string; segments: string[] }[] = namespaces.length
        ? namespaces.map((n) => ({ ns: n, segments: parts }))
        : [{ ns: parts[0], segments: parts.slice(1) }];

      for (const attempt of attempts) {
        // Prefer memoized translations when namespaces are known; otherwise parse on-demand
        let translations: unknown | undefined = translationsByNs.get(attempt.ns);
        if (!translations && namespaces.length === 0) {
          const edge = findLocaleEdge(attempt.ns, languageToUse, DEFAULT_LANG);
          if (!edge) continue;
          try {
            translations = JSON.parse(edge.node.data);
          } catch {
            continue;
          }
        }
        if (!translations) continue;

        const raw = traversePath(translations, attempt.segments);

        const value = typeof raw === "string" ? raw : undefined;
        if (typeof value === "string") {
          if (options) {
            return value.replace(/{{\s*(.+?)\s*}}/g, (_, varName: string) => {
              const v = (options as Record<string, unknown>)[varName];

              return v != null ? String(v) : "";
            });
          }

          return value;
        }
      }

      return (options?.defaultValue as string) || (inputKey as string);
    },
    [namespaces, translationsByNs, languageToUse, findLocaleEdge],
  ) as TFunction;

  return {
    language: detectedLanguage,
    routed,
    languages: localesArray,
    originalPath,
    t,
  };
};
